import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "@/lib/api/client";
import { DistrictDto, CreateDistrictDto, UpdateDistrictDto, StateDto, DistrictFilterParams } from "@/lib/types/api";
import { showToast } from "@/lib/utils/toast";
import { handleApiError } from "@/lib/utils/errors";
import { SEARCH_DEBOUNCE_MS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, Column } from "@/components/datatable/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, MapPinned, Search } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { LoadingButton } from "@/components/ui/loading";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/ui/status-badge";

const districtSchema = z.object({
  districtName: z.string().min(1, "District name is required"),
  lgdCode: z.string().regex(/^\d*$/,{ message: "LGD code must be numeric" }).optional(),
  stateId: z.string().min(1, "State is required"),
});

type DistrictFormData = z.infer<typeof districtSchema>;

export default function DistrictsPage() {
  const [districts, setDistricts] = useState<DistrictDto[]>([]);
  const [states, setStates] = useState<StateDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("districtName");
  const [sortDescending, setSortDescending] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DistrictFormData>({ resolver: zodResolver(districtSchema) });

  const loadDistricts = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const filterParams: DistrictFilterParams = {
        pageNumber: currentPage,
        pageSize: pageSize,
        searchTerm: searchQuery || undefined,
        sortBy: sortBy,
        sortDescending: sortDescending,
      };
      
      const response = await apiClient.getDistrictsPagedWithFilters(filterParams);
      
      if (response.success && response.data) {
        setDistricts(response.data.items || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(Math.ceil((response.data.totalCount || 0) / pageSize));
      }
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to load districts"));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, sortBy, sortDescending]);

  // Single effect to handle all data loading with proper debouncing
  useEffect(() => {
    // For page/pageSize changes, load immediately without debounce
    if (searchQuery === "" && sortBy === "districtName" && !sortDescending) {
      loadDistricts();
      return;
    }

    // For search/sort changes, debounce
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadDistricts();
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [currentPage, pageSize, searchQuery, sortBy, sortDescending, loadDistricts]);

  useEffect(() => {
    loadStates();
  }, []);

  const loadStates = async () => {
    try {
      const response = await apiClient.getStates();
      if (response.success && response.data) {
        setStates(response.data);
      }
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to load states"));
    }
  };

  const getStateName = (stateId: string) => {
    return states.find((s) => s.id === stateId)?.stateName || "Unknown";
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
  };

  const onSubmit = async (data: DistrictFormData) => {
    try {
      setIsSubmitting(true);
      if (editingId) {
        const updateData: UpdateDistrictDto = {
          districtName: data.districtName,
          lgdCode: data.lgdCode ? Number(data.lgdCode) : undefined,
          isActive: true,
        };
        await apiClient.updateDistrict(editingId, updateData);
        showToast.success("District updated successfully");
      } else {
        const createData: CreateDistrictDto = {
          districtName: data.districtName,
          lgdCode: data.lgdCode ? Number(data.lgdCode) : undefined,
          stateId: data.stateId,
        };
        await apiClient.createDistrict(createData);
        showToast.success("District created successfully");
      }
      setShowForm(false);
      setEditingId(null);
      reset();
      loadDistricts();
    } catch (err: unknown) {
      showToast.error(handleApiError(err, editingId ? "Failed to update district" : "Failed to create district"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (d: DistrictDto) => {
    setEditingId(d.id);
    reset({ districtName: d.districtName, lgdCode: d.lgdCode != null ? String(d.lgdCode) : "", stateId: d.stateId });
    setShowForm(true);
  };

  const handleDeleteRequest = (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await apiClient.deleteDistrict(deleteId);
      showToast.success("District deleted successfully");
      setDeleteId(null);
      loadDistricts();
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to delete district"));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    reset();
  };

  const handleOpenNew = () => {
    setEditingId(null);
    reset({ districtName: "", lgdCode: "", stateId: "" });
    setShowForm(true);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const columns: Column<DistrictDto>[] = [
    {
      key: "districtName",
      header: "District Name",
      sortable: true,
      render: (d) => <span className="font-medium text-gray-900 dark:text-gray-100">{d.districtName}</span>,
    },
    {
      key: "lgdCode",
      header: "LGD Code",
      sortable: true,
      render: (d) => <span className="text-sm text-gray-600 dark:text-gray-400">{d.lgdCode ?? <span className="text-gray-400">—</span>}</span>,
    },
    {
      key: "state",
      header: "State",
      sortable: true,
      render: (d) => <span className="text-sm text-gray-600 dark:text-gray-400">{getStateName(d.stateId)}</span>,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (d) => <StatusBadge isActive={d.isActive} />,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      render: (d) => <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(d.createdAt)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <Dialog open={showForm} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent onClose={handleCancel}>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit District" : "New District"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="District Name"
              placeholder="e.g., Central"
              error={errors.districtName?.message}
              {...register("districtName")}
            />
            <Input
              label="LGD Code"
              placeholder="Optional code"
              error={errors.lgdCode?.message}
              {...register("lgdCode")}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
              <Select value={""}>
                <SelectTrigger className="hidden"></SelectTrigger>
              </Select>
              <select
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                {...register("stateId")}
              >
                <option value="">Select a state</option>
                {states.map((s) => (
                  <option key={s.id} value={s.id}>{s.stateName}</option>
                ))}
              </select>
              {errors.stateId && <p className="text-red-500 text-sm mt-1">{errors.stateId.message}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>Cancel</Button>
              <LoadingButton type="submit" isLoading={isSubmitting} className="bg-primary px-4 py-2 rounded hover:bg-primary/90">
                {editingId ? "Update" : "Create"}
              </LoadingButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent onClose={() => setDeleteId(null)}>
          <DialogHeader>
            <DialogTitle>Delete District</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-400">Are you sure you want to delete this district? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="shadow-sm">
        <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <MapPinned className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-2xl">Districts</CardTitle>
                <CardDescription className="mt-1">Manage district information</CardDescription>
              </div>
            </div>
            <Button onClick={handleOpenNew} className="shadow-sm">
              <Plus className="mr-2 h-4 w-4" />
              Add District
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Search</h3>
              {totalCount > 0 && (
                <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  <span>{totalCount}</span>
                  <span>Total Districts</span>
                </div>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search districts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
            {searchQuery && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs">
                    Search: {searchQuery}
                    <button onClick={() => setSearchQuery("")} className="hover:text-blue-900 dark:hover:text-blue-100">×</button>
                  </span>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>

          <DataTable
            data={districts}
            columns={columns}
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            loading={isLoading}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onPageSizeChange={(size) => setPageSize(size)}
            onRefresh={loadDistricts}
            getRowKey={(d) => d.id}
            actionsRenderer={(d) => (
              <div className="flex justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEdit(d)}
                  className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Edit District"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteRequest(d.id)}
                  className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400"
                  title="Delete District"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            emptyMessage={searchQuery ? "No districts match your search" : "No districts found"}
          />
        </CardContent>
      </Card>
    </div>
  );
}
